import { AutoPayFormType } from '../../components/modals/AutoPayAuthorizationModal';
import { accountNumberValidation, bankNameValidation } from '../../components/validations/validations';
import { ContractApiResponse } from '../../services/contract-services';
import { openToast } from '../../store/reducers/toastSlice';
import { formatCurrency, formatInET } from '../../utils/helper';

import { AmortizationDataItem } from './amortizationSchedule';

import type { MouseEvent, SyntheticEvent } from 'react';
import type { Dispatch } from 'redux';

export interface AmortizationGrandTotals {
    amount: number;
    principal: number;
    interest: number;
}

export interface AmortizationScheduleRow {
    date: string;
    eventType: string;
    principal: string;
    interest: string;
    amount: string;
    balance: string;
}

export const calculateAmortizationGrandTotals = (data: AmortizationDataItem[]): AmortizationGrandTotals =>
    data.reduce(
        (totals, item) => {
            if (!item.EventType?.toLowerCase?.()?.includes('total')) {
                return totals;
            }
            return {
                amount: totals.amount + (Number(item.Amount) || 0),
                principal: totals.principal + (Number(item.PrincipleAmount) || 0),
                interest: totals.interest + (Number(item.InterestAmount) || 0),
            };
        },
        { amount: 0, principal: 0, interest: 0 },
    );

// Generic CSV builder shared across export flows (amortization schedule, payment history, etc).
// `totalRowCells` are pre-formatted cell strings for the trailing total row.
export const buildCsvContent = (
    columns: { id: string; label: string }[],
    rows: Record<string, unknown>[],
    totalRowCells: string[],
): string => {
    const headers = columns.map(col => col.label).join(',');

    const csvRows = rows.map(row => {
        const rowCells = columns.map(col => {
            const value = row[col.id] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
        });
        return rowCells.join(',');
    });

    return [headers, ...csvRows, totalRowCells.join(',')].join('\n');
};

// Generic XLSX (array-of-arrays) builder shared across export flows.
export const buildXlsxData = <T>(
    headerLabels: (string | number)[],
    rows: T[],
    rowMapper: (row: T) => (string | number)[],
    totalRowCells: (string | number)[],
): (string | number)[][] => [headerLabels, ...rows.map(rowMapper), totalRowCells];

export const buildAmortizationCsvContent = (
    transformedSchedule: Record<string, unknown>[],
    grandTotals: AmortizationGrandTotals,
    columns: { id: string; label: string }[],
) => {
    const grandTotalRow = [
        '',
        'Grand Total',
        `"${formatCurrency(grandTotals.amount)}"`,
        `"${formatCurrency(grandTotals.principal)}"`,
        `"${formatCurrency(grandTotals.interest)}"`,
        '""',
    ];

    return buildCsvContent(columns, transformedSchedule, grandTotalRow);
};

export const buildAmortizationXlsxData = (
    transformedSchedule: AmortizationScheduleRow[],
    grandTotals: AmortizationGrandTotals,
    headerLabels: string[],
) =>
    buildXlsxData(
        headerLabels,
        transformedSchedule,
        schedule => [
            schedule.date,
            schedule.eventType,
            schedule.amount,
            schedule.principal,
            schedule.interest,
            schedule.balance,
        ],
        [
            '',
            'Grand Total',
            formatCurrency(grandTotals.amount),
            formatCurrency(grandTotals.principal),
            formatCurrency(grandTotals.interest),
            '',
        ],
    );

export const buildAmortizationPdfTableRows = (transformedSchedule: AmortizationScheduleRow[]) =>
    transformedSchedule
        .map(
            schedule => `
        <tr ${schedule.eventType === 'Annual Total' ? ' style="font-weight: bold"' : ''}>
            <td>${schedule.date}</td>
            <td>${schedule.eventType}</td>
            <td>${schedule.amount}</td>
            <td>${schedule.principal}</td>
            <td>${schedule.interest}</td>
            <td>${schedule.balance}</td>
        </tr>
    `,
        )
        .join('');

export const buildAmortizationPdfGrandTotalRow = (grandTotals: AmortizationGrandTotals) => `
    <tr style="font-weight: bold; border-top: 2px solid #333;">
        <td colspan="2"><strong>Grand Total</strong></td>
        <td>${formatCurrency(grandTotals.amount)}</td>
        <td>${formatCurrency(grandTotals.principal)}</td>
        <td>${formatCurrency(grandTotals.interest)}</td>
        <td></td>
    </tr>
`;

export const buildAmortizationPdfHtml = (
    transformedSchedule: AmortizationScheduleRow[],
    grandTotals: AmortizationGrandTotals,
    columns: { id: string; label: string }[],
    contract: { AspireId?: string | null; Id?: string | null } | null,
) => {
    const tableHeaders = columns.map(col => `<th>${col.label}</th>`).join('');
    const tableRows = buildAmortizationPdfTableRows(transformedSchedule);
    const grandTotalRow = buildAmortizationPdfGrandTotalRow(grandTotals);
    const contractLabel = contract?.AspireId || contract?.Id || 'N/A';

    return `
        <html>
            <head>
                <title>Amortization Schedule - ${contract?.AspireId || 'Contract'}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                    }
                    h1 {
                        margin-bottom: 20px;
                        color: #333;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    th, td {
                        border-bottom: 1px solid #ddd;
                        padding: 10px;
                        text-align: left;
                    }
                    th {
                        background-color: #f2f2f2;
                        font-weight: bold;
                    }
                </style>
            </head>
            <body>
                <h1>Amortization Schedule</h1>
                <p><strong>Contract ID:</strong> ${contractLabel}</p>
                <p><strong>Date:</strong> ${formatInET(new Date())}</p>
                <table>
                    <thead>
                        <tr>${tableHeaders}</tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                        ${grandTotalRow}
                    </tbody>
                </table>
            </body>
        </html>
    `;
};

export const downloadBlob = (blob: Blob, filename: string) => {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const downloadTextFile = (content: string, filename: string, mimeType: string) => {
    downloadBlob(new Blob([content], { type: mimeType }), filename);
};

// Locates the export trigger button either from the click event, or (when invoked
// programmatically, e.g. after selecting a format) by scanning the DOM for the export
// button that contains the given icon class. Shared by the amortization schedule and
// payment history export menus.
export const findExportButtonElement = (
    event: MouseEvent<HTMLElement> | SyntheticEvent | undefined,
    iconClass: string,
): HTMLElement | null => {
    if (event && typeof event === 'object' && 'currentTarget' in event) {
        return event.currentTarget as HTMLElement;
    }

    const buttons = document.querySelectorAll('button');
    return (
        (Array.from(buttons).find(
            btn => btn.textContent?.includes('Export') && btn.textContent?.includes(iconClass),
        ) as HTMLElement) || null
    );
};

// Shared error-toast dispatch used by the CSV/XLSX/PDF export handlers.
export const dispatchExportError = (dispatch: Dispatch, err: unknown, fallbackMessage: string): void => {
    const e = err as { message?: string };
    dispatch(
        openToast({
            show: true,
            message: e?.message || fallbackMessage,
            type: 'error',
        }),
    );
};

export const getFormType = (contract: ContractApiResponse, isConsumer: boolean): AutoPayFormType => {
    if (isConsumer && contract.IsLoan) return 'consumer-loan';
    if (isConsumer && !contract.IsLoan) return 'consumer-lease';
    if (!isConsumer && contract.IsLoan) return 'commercial-loan';
    return 'commercial-lease';
};

export function validateRoutingNumber(rn: string): string {
    if (!rn) return 'Routing Number is required';
    if (!/^\d+$/.test(rn)) return 'Routing Number must contain only numbers';
    if (rn.length !== 9) return 'Routing Number must be exactly 9 digits';
    if (/^0+$/.test(rn)) return 'Routing Number cannot contain only zeros';
    if (/^(\d)\1+$/.test(rn)) return 'Routing Number cannot contain only the same digit (e.g., 111111111)';
    return '';
}

export const validateAddBankForm = (
    formData: {
        accountType: string;
        bankName: string;
        accountNumber: string;
        reEnterAccountNumber: string;
        routingNumber: string;
        reEnterRoutingNumber: string;
    },
    country: string,
): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!country || country.trim() === '') errors.country = 'Country is required';
    if (!formData.accountType) errors.accountType = 'Account Type is required';

    const bankNameError = bankNameValidation(formData.bankName);
    if (bankNameError) errors.bankName = bankNameError;

    const accountNumberError = accountNumberValidation(formData.accountNumber);
    const isAccountNumberValid = !accountNumberError;
    if (accountNumberError) {
        errors.accountNumber = accountNumberError;
    }
    const reEnterError = accountNumberValidation(formData.reEnterAccountNumber);
    if (reEnterError) {
        errors.reEnterAccountNumber = reEnterError;
    } else if (isAccountNumberValid && formData.accountNumber.trim() !== formData.reEnterAccountNumber.trim()) {
        errors.reEnterAccountNumber = 'Account Numbers do not match';
    } else if (!isAccountNumberValid) {
        errors.reEnterAccountNumber = 'Please fix the Account Number field first';
    }

    const rn = formData.routingNumber.trim();
    const routingNumberError = validateRoutingNumber(rn);
    if (routingNumberError) errors.routingNumber = routingNumberError;

    const rrn = formData.reEnterRoutingNumber.trim();
    if (!rrn) {
        errors.reEnterRoutingNumber = 'Re-enter Routing Number is required';
    } else if (formData.routingNumber !== formData.reEnterRoutingNumber) {
        errors.reEnterRoutingNumber = 'Routing Numbers do not match';
    } else if (rrn.length !== 9) {
        errors.reEnterRoutingNumber = 'Routing Number must be exactly 9 digits';
    }
    return errors;
};
